import { type ExamOption, type ExamOptionGroup } from "@/types/examSelection";

interface BuildExamGroupsOptions {
  groupCombinedStateNames?: boolean;
  orderedCodes: string[];
  stateExamNames?: Record<string, string>;
  stateNames?: Record<string, string>;
}

function createExamLabel(examName: string, stateName: string) {
  const withoutState = examName
    .replace(new RegExp(`^${stateName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*[-–—]?\\s*`, "i"), "")
    .trim();

  return withoutState || examName || stateName;
}

export function getExamDisplayNames(examName: string, rawStateName: string) {
  const separatedName = rawStateName.match(/^(.*?)\s+[-–—]\s+(.+)$/);
  const stateName = separatedName?.[1]?.trim() || rawStateName;
  const examLabel = separatedName?.[2]?.trim() || createExamLabel(examName, stateName);

  return { examLabel, stateName };
}

export function buildExamOptionGroups({
  groupCombinedStateNames = false,
  orderedCodes,
  stateExamNames = {},
  stateNames = {},
}: BuildExamGroupsOptions): ExamOptionGroup[] {
  const groups = new Map<string, ExamOptionGroup>();

  orderedCodes.forEach((code) => {
    const rawStateName = stateNames[code] || code;
    const examName = stateExamNames[code] || rawStateName;
    const { examLabel, stateName } = groupCombinedStateNames
      ? getExamDisplayNames(examName, rawStateName)
      : { examLabel: createExamLabel(examName, rawStateName), stateName: rawStateName };
    const group = groups.get(stateName) || { stateName, exams: [] };
    group.exams.push({
      code,
      examName,
      label: examLabel,
      stateName,
    });
    groups.set(stateName, group);
  });

  return Array.from(groups.values());
}

export function getSoleExamOption(
  groups: ExamOptionGroup[],
  stateName: string,
): ExamOption | null {
  const exams = groups.find((group) => group.stateName === stateName)?.exams;
  return exams?.length === 1 ? exams[0] : null;
}
